import { useEffect, useId, useState } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AccountInlinePicker, type PickedAccount } from "@/components/account/AccountInlinePicker";
import { FieldError, MissingFieldsSummary, SubmitErrorNotice } from "@/components/common/FormFeedback";
import { useCreateComplaint } from "@/hooks/useComplaints";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useRequiredFields } from "@/hooks/useRequiredFields";
import { cn } from "@/lib/cn";
import { describeApiError, type FriendlyError } from "@/lib/apiErrorMessages";
import { fetchBrands, type BrandRef } from "@/api/businessProfile";
import type {
  ComplaintChannel,
  ComplaintProductType,
  ComplaintSeverity,
  ComplaintType,
} from "@/api/types";
import {
  CHANNEL_LABEL,
  CONDITIONAL_REQUIRED_TYPES,
  OPTIONAL_PRODUCT_TYPES,
  PRODUCT_LINKED_TYPES,
  PRODUCT_TYPE_LABEL,
  SEVERITY_LABEL,
  TYPE_LABEL,
} from "./constants";

// Ordered for the picker: [M] bucket, then [SQ] bucket, then triage.
const TYPES: ComplaintType[] = [
  "simple_service",
  "document_error",
  "delivery_logistics",
  "order_error",
  "pricing_terms",
  "packaging_damage_no_quality",
  "product_quality",
  "food_quality",
  "batch_issue",
  "expiry_issue",
  "health_risk",
  "recurring_sku_issue",
  "other",
];
const PRODUCT_TYPES: ComplaintProductType[] = [
  "food",
  "treats",
  "litter",
  "hygiene",
  "accessory",
  "veterinary_product",
  "other",
];
const BRAND_OTHER = "__other__";

function nowLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function CreateComplaintDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();

  const [account, setAccount] = useState<PickedAccount | null>(null);
  const [accSearch, setAccSearch] = useState("");
  const [accOpen, setAccOpen] = useState(false);

  const [type, setType] = useState<ComplaintType>("simple_service");
  const [severity, setSeverity] = useState<ComplaintSeverity>("medium");
  const [channel, setChannel] = useState<ComplaintChannel>("phone");
  const [receivedAt, setReceivedAt] = useState(nowLocal());
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");

  const [linkProduct, setLinkProduct] = useState(false);
  const [productType, setProductType] = useState<ComplaintProductType | "">("");
  const [article, setArticle] = useState("");
  const [productName, setProductName] = useState("");
  const [batch, setBatch] = useState("");
  const [batchUnknown, setBatchUnknown] = useState(false);
  const [expiry, setExpiry] = useState("");
  const [affectedQty, setAffectedQty] = useState("");

  // brand: catalog pick (biBrandId) OR free-text fallback (brand_unverified)
  const [brands, setBrands] = useState<BrandRef[]>([]);
  const [brandsDown, setBrandsDown] = useState(false);
  const [brandSel, setBrandSel] = useState(""); // "" | biBrandId | BRAND_OTHER
  const [brandFree, setBrandFree] = useState("");

  const [error, setError] = useState<FriendlyError | null>(null);
  const mut = useCreateComplaint();

  // §0 — «Створити чернетку» never disabled for gaps: press → list + jump to the gap.
  const req = useRequiredFields([
    {
      key: "account",
      label: "Клієнт",
      missing: !account,
      message: accSearch.trim() ? "Оберіть клієнта зі списку" : "Оберіть клієнта",
    },
    { key: "summary", label: "Короткий опис", missing: summary.trim() === "", message: "Заповніть короткий опис" },
    { key: "description", label: "Детальний опис", missing: description.trim() === "", message: "Заповніть детальний опис" },
  ]);
  const resetRequired = req.reset;

  const showProduct =
    PRODUCT_LINKED_TYPES.includes(type) || (OPTIONAL_PRODUCT_TYPES.includes(type) && linkProduct);
  const isCriticalProduct = CONDITIONAL_REQUIRED_TYPES.includes(type);
  const brandUnverified = brandSel === BRAND_OTHER || (brandsDown && brandSel === "");

  useEffect(() => {
    if (open) {
      setAccount(null);
      setAccSearch("");
      setType("simple_service");
      setSeverity("medium");
      setChannel("phone");
      setReceivedAt(nowLocal());
      setSummary("");
      setDescription("");
      setLinkProduct(false);
      setProductType("");
      setArticle("");
      setProductName("");
      setBatch("");
      setBatchUnknown(false);
      setExpiry("");
      setAffectedQty("");
      setBrandSel("");
      setBrandFree("");
      setError(null);
      resetRequired();
    }
  }, [open, resetRequired]);

  // Load the KormaxBrand catalog when the product block is visible.
  useEffect(() => {
    if (!open || !showProduct || brands.length > 0 || brandsDown) return;
    const ctrl = new AbortController();
    fetchBrands(undefined, ctrl.signal)
      .then((r) => setBrands(r.items.filter((b) => !b.inactive)))
      .catch(() => setBrandsDown(true)); // catalog unavailable → free-text fallback
    return () => ctrl.abort();
  }, [open, showProduct, brands.length, brandsDown]);

  if (!open) return null;

  function brandPayload(): {
    cCBrandBiId?: string;
    cCBrand?: string;
    cCBrandUnverified?: boolean;
  } {
    if (brandSel && brandSel !== BRAND_OTHER) return { cCBrandBiId: brandSel };
    if (brandUnverified && brandFree.trim()) {
      return { cCBrand: brandFree.trim(), cCBrandUnverified: true };
    }
    return {};
  }

  async function submit() {
    setError(null);
    if (!req.validate() || !account) return;
    try {
      const detail = await mut.mutateAsync({
        accountId: account.id,
        summary: summary.trim(),
        description: description.trim(),
        complaintType: type,
        severity,
        receivedVia: channel,
        receivedAt: receivedAt.replace("T", " ") + ":00",
        cCLinkedToProduct: OPTIONAL_PRODUCT_TYPES.includes(type) ? linkProduct : undefined,
        ...(showProduct && {
          cCProductType: (productType || undefined) as ComplaintProductType | undefined,
          cCProductArticle: article.trim() || undefined,
          cCProductName: productName.trim() || undefined,
          ...brandPayload(),
          cCBatch: batch.trim() || undefined,
          cCBatchUnknown: batchUnknown || undefined,
          cCExpiryDate: expiry || undefined,
          cCAffectedQuantity: affectedQty.trim() || undefined,
        }),
      });
      onCreated(detail.id);
      onClose();
    } catch (e) {
      setError(describeApiError(e, "Не вдалося створити рекламацію"));
    }
  }

  const reqMark = isCriticalProduct ? <span className="text-destructive"> *</span> : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} role="presentation">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full overflow-y-auto bg-background shadow-2xl outline-none sm:w-[480px]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="text-base font-semibold">
            Нова рекламація
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div ref={req.fieldRef("account")}>
            <label className="mb-1 block text-xs font-medium">
              Клієнт <span className="text-destructive">*</span>
            </label>
            {account ? (
              <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2 text-sm">
                <span className="truncate">{account.name}</span>
                <button
                  type="button"
                  onClick={() => setAccount(null)}
                  className="text-xs text-primary hover:underline"
                >
                  Змінити
                </button>
              </div>
            ) : (
              <AccountInlinePicker
                query={accSearch}
                onQueryChange={setAccSearch}
                isOpen={accOpen || accSearch.length >= 2}
                onOpen={() => setAccOpen(true)}
                onPick={(a) => {
                  setAccount(a);
                  setAccSearch("");
                  setAccOpen(false);
                }}
                error={req.errorFor("account")}
                errorId={req.errorId("account")}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium">Тип</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as ComplaintType)}
                className="h-10 w-full rounded-md border bg-background px-2 text-sm"
              >
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Важливість</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as ComplaintSeverity)}
                className="h-10 w-full rounded-md border bg-background px-2 text-sm"
              >
                {(["low", "medium", "high", "critical"] as const).map((s) => (
                  <option key={s} value={s}>
                    {SEVERITY_LABEL[s]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Канал</label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value as ComplaintChannel)}
                className="h-10 w-full rounded-md border bg-background px-2 text-sm"
              >
                {(Object.keys(CHANNEL_LABEL) as ComplaintChannel[]).map((ch) => (
                  <option key={ch} value={ch}>
                    {CHANNEL_LABEL[ch]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Отримано</label>
              <input
                type="datetime-local"
                value={receivedAt}
                onChange={(e) => setReceivedAt(e.target.value)}
                className="h-10 w-full rounded-md border bg-background px-2 text-sm"
              />
            </div>
          </div>

          <div ref={req.fieldRef("summary")}>
            <label className="mb-1 block text-xs font-medium">
              Короткий опис <span className="text-destructive">*</span>
            </label>
            <input
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              maxLength={200}
              {...req.invalidProps("summary")}
              className={cn(
                "h-10 w-full rounded-md border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                req.errorFor("summary") && "border-destructive",
              )}
            />
            <FieldError id={req.errorId("summary")} message={req.errorFor("summary")} />
          </div>

          <div ref={req.fieldRef("description")}>
            <label className="mb-1 block text-xs font-medium">
              Детальний опис <span className="text-destructive">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              {...req.invalidProps("description")}
              className={cn(
                "w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                req.errorFor("description") && "border-destructive",
              )}
            />
            <FieldError id={req.errorId("description")} message={req.errorFor("description")} />
          </div>

          {OPTIONAL_PRODUCT_TYPES.includes(type) && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={linkProduct}
                onChange={(e) => setLinkProduct(e.target.checked)}
                className="h-4 w-4 rounded border"
              />
              Пов’язано з товаром
            </label>
          )}

          {showProduct && (
            <div className="space-y-3 rounded-md border bg-muted/20 p-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Контекст товару
                </div>
                {isCriticalProduct && (
                  <span className="rounded bg-warning px-1.5 py-0.5 text-[10px] font-semibold text-warning-foreground">
                    обов’язково на подачу
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium">Тип товару</label>
                  <select
                    value={productType}
                    onChange={(e) => setProductType(e.target.value as ComplaintProductType | "")}
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  >
                    <option value="">— виберіть —</option>
                    {PRODUCT_TYPES.map((p) => (
                      <option key={p} value={p}>
                        {PRODUCT_TYPE_LABEL[p]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">Артикул (SKU){reqMark}</label>
                  <input
                    value={article}
                    onChange={(e) => setArticle(e.target.value)}
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="mb-1 block text-xs font-medium">Назва товару{reqMark}</label>
                  <input
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  />
                </div>

                {/* brand — catalog pick (biBrandId) or unverified free-text fallback */}
                <div className="col-span-2">
                  <label className="mb-1 block text-xs font-medium">Бренд{reqMark}</label>
                  {brandsDown ? (
                    <div className="space-y-1">
                      <input
                        value={brandFree}
                        onChange={(e) => setBrandFree(e.target.value)}
                        placeholder="Бренд (каталог недоступний)"
                        className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                      />
                      <p className="text-[11px] text-warning-foreground">
                        Каталог брендів недоступний — буде позначено «не підтверджено» та поставлено в чергу звірки.
                      </p>
                    </div>
                  ) : (
                    <>
                      <select
                        value={brandSel}
                        onChange={(e) => setBrandSel(e.target.value)}
                        className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                      >
                        <option value="">— виберіть бренд —</option>
                        {brands.map((b) => (
                          <option key={b.biBrandId} value={b.biBrandId}>
                            {b.name}
                          </option>
                        ))}
                        <option value={BRAND_OTHER}>Інший бренд (не підтверджено)…</option>
                      </select>
                      {brandSel === BRAND_OTHER && (
                        <input
                          value={brandFree}
                          onChange={(e) => setBrandFree(e.target.value)}
                          placeholder="Назва бренду (буде позначено «не підтверджено»)"
                          className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm"
                        />
                      )}
                    </>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium">Партія{reqMark}</label>
                  <input
                    value={batch}
                    onChange={(e) => setBatch(e.target.value)}
                    disabled={batchUnknown}
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">Термін придатності{reqMark}</label>
                  <input
                    type="date"
                    value={expiry}
                    onChange={(e) => setExpiry(e.target.value)}
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">Кількість</label>
                  <input
                    value={affectedQty}
                    onChange={(e) => setAffectedQty(e.target.value)}
                    placeholder="3 шт / 2 коробки"
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={batchUnknown}
                  onChange={(e) => setBatchUnknown(e.target.checked)}
                  className="h-4 w-4 rounded border"
                />
                Партія невідома (створити завдання-уточнення)
              </label>
              {isCriticalProduct && (
                <p className="text-[11px] text-muted-foreground">
                  Для критичних типів (корм/партія/термін/здоров’я) бренд, артикул або назва, партія, термін і ≥1 фото
                  обов’язкові на подачу. У чернетці можна зберегти частково.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 border-t bg-background px-5 py-4">
          <MissingFieldsSummary missing={req.missing} onJump={req.focusField} />
          <SubmitErrorNotice error={error} />
          <Button onClick={submit} disabled={mut.isPending} className="h-11 w-full text-sm font-semibold">
            {mut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Створення…
              </>
            ) : (
              "Створити чернетку"
            )}
          </Button>
          <p className="mt-1.5 text-center text-xs text-muted-foreground">
            Створює чернетку. Подача — на сторінці деталей.
          </p>
        </div>
      </div>
    </div>
  );
}
